import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { NavBar, Cell, Tag, Loading, Empty, Progress, Button } from 'react-vant'
import { ArrowLeft, Description } from '@react-vant/icons'
import request from '../../api/request'
import { DETAIL_ITEM_NAMES as itemNames } from '../../utils/constants'
import ElectronicReport from '../../components/ElectronicReport'

export default function PhysicalTestDetail() {
    const { testId } = useParams()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(true)
    const [test, setTest] = useState(null)
    const [generatingAdvice, setGeneratingAdvice] = useState(false)
    const [aiExpanded, setAiExpanded] = useState(false)
    const [reportVisible, setReportVisible] = useState(false)

    const formatYear = (yr) => {
        if (!yr) return '';
        if (typeof yr !== 'string') return `${yr}学年`;
        if (yr.includes('学年')) return yr;
        const match = yr.match(/^(\d{4}-\d{4})(.*)$/);
        if (match) {
            return `${match[1]}学年${match[2]}`;
        }
        return `${yr}学年`;
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true)
            try {
                const res = await request.get(`/sports/tests/${testId}`)
                setTest(res)
            } catch (e) {
                console.error('获取成绩详情失败', e)
            } finally {
                setLoading(false)
            }
        }
        fetchData()
    }, [testId])

    const getScoreColor = (score) => {
        if (score >= 90) return '#00b578'
        if (score >= 80) return '#056b4f'
        if (score >= 60) return '#fa8c16'
        return '#e11d48'
    }

    const formatScoreValue = (key, value) => {
        if (value === null || value === undefined || value === '') return '--'
        if (['run_50m_8x', 'run50m8x', 'run_800m', 'run800m', 'run_1000m', 'run1000m'].includes(key) && typeof value === 'number') {
            if (value < 10) {
                const minutes = Math.floor(value)
                const secondsStr = value.toFixed(2).split('.')[1]
                return `${minutes}分${secondsStr}秒`
            }
            const m = Math.floor(value / 60)
            const s = Math.round(value % 60)
            return `${m}分${s.toString().padStart(2, '0')}秒`
        }
        return value
    }

    const handleGenerateAdvice = async () => {
        if (!test || !test.student_id) return
        setGeneratingAdvice(true)
        try {
            const res = await request.get(`/sports/ai/advice/${test.student_id}`)
            if (res.success) {
                // Update local state, database persistence is already handled by backend
                setTest(prev => ({ ...prev, ai_advice: res.advice }))
            } else if (res.ai_disabled) {
                setTest(prev => ({ ...prev, ai_advice: res.advice }))
            }
        } catch (e) {
            console.error('获取专家建议失败', e)
        } finally {
            setGeneratingAdvice(false)
        }
    }

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Loading type="spinner">加载中...</Loading>
            </div>
        )
    }

    if (!test) {
        return (
            <div className="page-container">
                <NavBar title="成绩详情" leftArrow={<ArrowLeft />} onClickLeft={() => navigate(-1)} />
                <Empty description="找不到该成绩记录" />
            </div>
        )
    }

    const items = test.item_scores || {}

    return (
        <div className="page-container" style={{ background: 'var(--canvas-bg, #f8fafc)', minHeight: '100vh' }}>
            <NavBar
                title="成绩详情"
                leftArrow={<ArrowLeft />}
                onClickLeft={() => navigate(-1)}
                fixed
                placeholder
            />

            <div style={{ background: '#fff', padding: '24px 16px', textAlign: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: 14, color: '#94a3b8', marginBottom: 12 }}>{test.student_name} | {test.test_date}</div>

                {/* 三项成绩并排显示 */}
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: 16, marginBottom: 12 }}>
                    {/* 标准分 */}
                    <div style={{ flex: 1, textAlign: 'center' }}>
                        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>标准分</div>
                        <div style={{ fontSize: 28, fontWeight: 'bold', color: 'var(--primary-color, #056b4f)' }}>
                            {(() => {
                                const standard = test.composite_score
                                    ? (test.composite_score - (test.bonus_score || 0))
                                    : (test.total_score || 0);
                                return Number(standard.toFixed(1));
                            })()}
                        </div>
                    </div>
                    {/* 加分 */}
                    <div style={{ flex: 1, textAlign: 'center' }}>
                        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>加分</div>
                        <div style={{ fontSize: 28, fontWeight: 'bold', color: '#d97706' }}>
                            {test.bonus_score || 0}
                        </div>
                    </div>
                    {/* 学年总分 */}
                    <div style={{ flex: 1, textAlign: 'center' }}>
                        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 4 }}>学年总分</div>
                        <div style={{ fontSize: 28, fontWeight: 'bold', color: getScoreColor(test.composite_score || test.total_score || 0) }}>
                            {test.composite_score || test.total_score || 0}
                        </div>
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                    <Tag type={test.total_score >= 90 ? 'success' : (test.total_score >= 80 ? 'primary' : (test.total_score >= 60 ? 'warning' : 'danger'))}>
                        {test.grade || '未评定'}
                    </Tag>
                    <Tag plain type="primary">{formatYear(test.academic_year)}</Tag>
                </div>
            </div>

            {test.ai_advice ? (
                <div style={{ margin: '0 16px 12px', background: 'linear-gradient(145deg, #f0fdf4, #d1fae5)', padding: '16px', borderRadius: '12px', color: '#065f46', fontSize: '14px', lineHeight: '1.6', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', top: '-10px', right: '-10px', fontSize: '60px', opacity: 0.1 }}>🤖</div>
                    <div style={{ fontWeight: 'bold', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span>✨</span>专家综合评估与健康建议
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap', ...(aiExpanded ? {} : { display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical', overflow: 'hidden' }) }}>
                        {test.ai_advice}
                    </div>
                    {test.ai_advice.length > 100 && (
                        <div
                            style={{ textAlign: 'center', marginTop: '8px', color: '#065f46', fontWeight: 'bold', borderTop: '1px solid rgba(22, 101, 52, 0.1)', paddingTop: '8px' }}
                            onClick={() => setAiExpanded(!aiExpanded)}
                        >
                            {aiExpanded ? '收起建议 ∧' : '展开全文 ∨'}
                        </div>
                    )}
                </div>
            ) : (
                <div style={{ margin: '0 16px 12px' }}>
                    <Button
                        block
                        round
                        color="linear-gradient(to right, #056b4f, #04523c)"
                        loading={generatingAdvice}
                        onClick={handleGenerateAdvice}
                        style={{ boxShadow: '0 4px 12px rgba(0, 181, 120, 0.2)' }}
                    >
                        ✨ 点击获取 AI 专家针对性建议
                    </Button>
                </div>
            )}

            <div className="section-title" style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 14 }}>各单项得分</div>
            <Cell.Group inset>
                {Object.keys(items).map(key => {
                    const item = items[key]
                    if (typeof item !== 'object' || item === null) return null

                    return (
                        <Cell
                            key={key}
                            title={itemNames[key] || key}
                            label={`测试值: ${formatScoreValue(key, item.value)}`}
                            value={
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ color: getScoreColor(item.score), fontWeight: 'bold' }}>{item.score}分</div>
                                    <div style={{ fontSize: 11, color: '#94a3b8' }}>
                                        {{
                                            'A': '优秀',
                                            'B': '良好',
                                            'C': '及格',
                                            'D': '不及格',
                                            'F': '不及格',
                                            '优秀': '优秀',
                                            '良好': '良好',
                                            '及格': '及格',
                                            '不及格': '不及格'
                                        }[item.grade] || item.grade || ''}
                                    </div>
                                </div>
                            }
                        />
                    )
                })}
            </Cell.Group>

            {test.test_notes && (
                <div style={{ margin: '12px 16px 0', background: '#fff', borderRadius: '12px', overflow: 'hidden' }}>
                    <div className="section-title" style={{ padding: '12px 16px', color: '#334155', fontSize: 14, borderBottom: '1px solid #e2e8f0', fontWeight: 'bold' }}>教师评语与备注</div>
                    <div style={{ padding: '16px', fontSize: 14, color: '#64748b', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                        {test.test_notes}
                    </div>
                </div>
            )}

            {test.comment && (
                <Cell.Group inset style={{ marginTop: 12 }} title="简短评语(系统)">
                    <Cell title={test.comment} />
                </Cell.Group>
            )}

            <div style={{ padding: '16px 16px 8px' }}>
                <Button
                    block
                    round
                    plain
                    type="primary"
                    icon={<Description />}
                    onClick={() => setReportVisible(true)}
                >
                    生成电子报告单
                </Button>
            </div>

            <div style={{ padding: '8px 16px 24px', fontSize: 13, color: '#94a3b8', lineHeight: 1.6 }}>
                注：成绩根据《国家学生体质健康标准》计算。其中包含身高体重(BMI)、肺活量、身体素质各项测试。
            </div>

            <ElectronicReport
                visible={reportVisible}
                onClose={() => setReportVisible(false)}
                studentName={test.student_name}
                gradeName={test.grade_name || ''}
                className={test.class_name || ''}
                educationId={test.education_id}
                record={test}
            />
        </div>
    )
}
